<template>
  <div class="main-nav" data-test-id="main-nav">
    <EButton
      ref="toggle"
      aria-label="Toggle menu"
      data-track-id="burger-menu"
      data-test-id="main-nav-toggle"
      icon="hamburger"
      :icon-size="iconSize"
      class="main-nav__button"
      @click="toggle"
    />
    <EPanel
      v-if="withPanel"
      v-on-clickaway="clickAway"
      :visible="visible"
      :side="panelSide"
      backdrop
      data-test-id="main-nav-panel"
      @click="hide"
    >
      <div class="panel-header">
        <EIcon icon="logo-compact" class="panel-header__icon" />
        <EButton
          icon="close"
          aria-label="Close"
          class="panel-close"
          variant="bare"
          size="s"
          data-test-id="main-nav-panel-close"
          @click="hide"
        />
      </div>
      <EMainNavMenu v-bind="menuProps" />
    </EPanel>
    <EDropdown
      v-else
      v-on-clickaway="clickAway"
      :visible="visible"
      class="main-nav__dropdown"
      side="right"
      data-test-id="main-nav-dropdown"
    >
      <EMainNavMenu v-bind="menuProps" />
    </EDropdown>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import debounce from 'lodash/debounce';

import { isDesktopUp, toggleScrollLock } from '~~/common/js/client/screen';
import { RESIZE_DEBOUNCE_DELAY } from '~~/common/js/constants/events';
import { sendCoreAnalyticsEvent } from '~~/common/js/client/analytics';
import EDropdown from '~~/common/vue/components/dropdown';
import EIcon from '~~/common/vue/components/icon';
import EButton from '~~/common/vue/components/button';
import EPanel from '~~/common/vue/components/panel';
import screen from '~~/common/vue/mixins/screen';
import { focusFocusableChild, findFocusableChildren } from '~~/common/js/client/keyboard-utils';
import EMainNavMenu from './menu';

export default {
  name: 'EMainNav',
  components: {
    EButton,
    EDropdown,
    EIcon,
    EMainNavMenu,
    EPanel,
  },
  mixins: [clickaway, screen()],
  props: {
    /* eslint-disable vue/require-default-prop */
    panelSide: EPanel.props.side,
    translationStringPrefix: EMainNavMenu.props.translationStringPrefix,
    translationStringSufix: EMainNavMenu.props.translationStringSufix,
    settingsUrl: EMainNavMenu.props.settingsUrl,
    /* eslint-enabled vue/require-default-prop */
  },
  data() {
    return {
      visible: false,
      withPanel: false,
    };
  },
  computed: {
    ...mapState('global', ['locale']),
    menuProps() {
      const { translationStringPrefix, translationStringSufix, settingsUrl } = this;
      return { translationStringPrefix, translationStringSufix, settingsUrl };
    },
    focusableChildren() {
      return findFocusableChildren(this.$el);
    },
    iconSize() {
      return this.isDesktopUp ? 's' : 'm';
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('keyup', this.onKeyUp);
    this.onResize = debounce(this.setWithPanel, RESIZE_DEBOUNCE_DELAY);
    window.addEventListener('resize', this.onResize);
    this.setWithPanel();
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('keyup', this.onKeyUp);
  },
  methods: {
    clickAway({ target }) {
      const { $el } = this.$refs.toggle;
      if (target !== $el && !$el.contains(target)) {
        this.hide();
      }
    },
    setVisible(value) {
      this.visible = value;
      if (this.visible) {
        this.$emit('visible');

        if (this.withPanel) {
          // When the menu is shown with panel, we want a fixed body
          // so the body won't scroll
          toggleScrollLock(true);
        }
      } else {
        // When the menu is hidden,
        // we want to remove the restore scrolling
        toggleScrollLock(false);
      }
    },
    hide() {
      this.setVisible(false);
      if (this.$el.contains(document.activeElement)) {
        this.$refs.toggle.$el.focus();
      }
    },
    onKeyUp({ which, keyCode }) {
      const code = keyCode || which;
      // Esc
      if (code === 27) {
        this.hide();
      }
    },
    onKeyDown({ target, which, keyCode }) {
      const code = keyCode || which;
      // Tab
      if (this.visible &&
      code === 9 &&
      target === this.focusableChildren[this.focusableChildren.length - 1]) {
        focusFocusableChild(this.$el);
      }
    },
    toggle(event) {
      sendCoreAnalyticsEvent('mainNavToggled');
      this.setVisible(!this.visible);
      if (this.visible) {
        event.currentTarget.focus();
      } else {
        event.currentTarget.blur();
      }
    },
    setWithPanel() {
      this.withPanel = !isDesktopUp();

      if (!this.withPanel && this.visible) {
        toggleScrollLock(false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.panel-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: $space-s;
  height: $space-4l + $space-s + $space-2s;
}

.panel-header__icon {
  height: $space-1l;
}

.main-nav {
  position: relative;
}

.main-nav__dropdown {
  margin-top: $space-s;
  right: -$space-s;
  color: var(--color-text-primary);
}
</style>
